import React from 'react';
import { Button, Card, Collapse, Form, Table } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { GitAdminIssue } from '../../interfaces';

type IssueMergeRequestMeta = {
  mergeRequestUrl: string;
  pipelineStatus?: string | null;
};

type Props = {
  issueStateFilter: 'opened' | 'closed' | 'all';
  setIssueStateFilter: (value: 'opened' | 'closed' | 'all') => void;
  showIssues: boolean;
  setShowIssues: React.Dispatch<React.SetStateAction<boolean>>;
  filteredIssues: GitAdminIssue[];
  refreshIssuesForUser: () => Promise<void>;
  repositoriesById: Map<number, { name?: string }>;
  buildIssueKey: (repositoryId: number, issueTitle?: string | null, issueOwner?: string | null) => string;
  buildIssueKeyWithoutOwner: (repositoryId: number, issueTitle?: string | null) => string;
  issueMergeRequestMetaByKey: Map<string, IssueMergeRequestMeta>;
  issueMergeRequestMetaByTitleKey: Map<string, IssueMergeRequestMeta>;
  renderMergeRequestPill: (meta?: IssueMergeRequestMeta) => React.ReactNode;
  renderTimeProgress: (timeSpentSeconds?: number | null, timeEstimateSeconds?: number | null) => React.ReactNode;
  setIssues: React.Dispatch<React.SetStateAction<GitAdminIssue[]>>;
  saveIssue: (issue: GitAdminIssue) => Promise<void>;
  showUnassignedIssues: boolean;
  setShowUnassignedIssues: React.Dispatch<React.SetStateAction<boolean>>;
  unassignedIssues: GitAdminIssue[];
};

const GitAdminIssuesCards: React.FC<Props> = ({
  issueStateFilter,
  setIssueStateFilter,
  showIssues,
  setShowIssues,
  filteredIssues,
  refreshIssuesForUser,
  repositoriesById,
  buildIssueKey,
  buildIssueKeyWithoutOwner,
  issueMergeRequestMetaByKey,
  issueMergeRequestMetaByTitleKey,
  renderMergeRequestPill,
  renderTimeProgress,
  setIssues,
  saveIssue,
  showUnassignedIssues,
  setShowUnassignedIssues,
  unassignedIssues
}) => {
  const { t } = useTranslation();

  return (
    <>
      <Card className="shadow-sm mb-4">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span>{t('gitAdmin.issues')}</span>
          <div className="d-flex align-items-center gap-2">
            <Form.Select
              className="gitadmin-issues-filter-select"
              size="sm"
              value={issueStateFilter}
              onChange={(event) => setIssueStateFilter(event.target.value as 'opened' | 'closed' | 'all')}
            >
              <option value="opened">Issues ouvertes</option>
              <option value="closed">Issues closes</option>
              <option value="all">Toutes les issues</option>
            </Form.Select>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={() => setShowIssues(value => !value)}
            >
              {showIssues ? 'Masquer' : 'Afficher'} ({filteredIssues.length})
            </Button>
            <Button size="sm" variant="outline-primary" onClick={refreshIssuesForUser}>
              Rafraichir mes issues
            </Button>
          </div>
        </Card.Header>
        <Collapse in={showIssues}>
          <div>
            <Card.Body className="table-responsive">
              <Table striped hover>
                <thead>
                  <tr>
                    <th>{t('name')}</th>
                    <th>{t('title')}</th>
                    <th>MR</th>
                    <th>{t('gitAdmin.issueOwner')}</th>
                    <th>Progress</th>
                    <th>State</th>
                    <th>{t('update')}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredIssues.map(issue => {
                    const repositoryName = repositoriesById.get(issue.gitRepositoryId)?.name ?? String(issue.gitRepositoryId);
                    const issueKey = buildIssueKey(issue.gitRepositoryId, issue.title, issue.assigneeName);
                    const issueTitleKey = buildIssueKeyWithoutOwner(issue.gitRepositoryId, issue.title);
                    const mergeRequestMeta = issueMergeRequestMetaByKey.get(issueKey) ?? issueMergeRequestMetaByTitleKey.get(issueTitleKey);

                    return (
                      <tr key={issue.id}>
                        <td>{repositoryName}</td>
                        <td>{issue.title}</td>
                        <td>{renderMergeRequestPill(mergeRequestMeta)}</td>
                        <td>{issue.assigneeName ?? 'N/A'}</td>
                        <td>{renderTimeProgress(issue.timeSpentSeconds, issue.timeEstimateSeconds)}</td>
                        <td>
                          <Form.Select
                            value={issue.state ?? 'opened'}
                            onChange={(event) => setIssues(prev => prev.map(item => item.id === issue.id ? { ...item, state: event.target.value } : item))}
                          >
                            <option value="opened">opened</option>
                            <option value="closed">closed</option>
                          </Form.Select>
                        </td>
                        <td>
                          <Button size="sm" onClick={() => saveIssue(issue)}>{t('update')}</Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      <Card className="shadow-sm mb-4">
        <Card.Header className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <span>Issues sans assignee</span>
          <Button
            size="sm"
            variant="outline-secondary"
            onClick={() => setShowUnassignedIssues(value => !value)}
          >
            {showUnassignedIssues ? 'Masquer' : 'Afficher'} ({unassignedIssues.length})
          </Button>
        </Card.Header>
        <Collapse in={showUnassignedIssues}>
          <div>
            <Card.Body className="table-responsive">
              {unassignedIssues.length === 0 ? (
                <div className="text-muted">Aucune issue sans assignee.</div>
              ) : (
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>{t('name')}</th>
                      <th>{t('title')}</th>
                      <th>Progress</th>
                      <th>State</th>
                      <th>Lien</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unassignedIssues.map(issue => {
                      const repositoryName = repositoriesById.get(issue.gitRepositoryId)?.name ?? String(issue.gitRepositoryId);

                      return (
                        <tr key={`unassigned-${issue.id}`}>
                          <td>{repositoryName}</td>
                          <td>{issue.title}</td>
                          <td>{renderTimeProgress(issue.timeSpentSeconds, issue.timeEstimateSeconds)}</td>
                          <td>{issue.state}</td>
                          <td>
                            {issue.webUrl ? (
                              <a href={issue.webUrl} target="_blank" rel="noopener noreferrer">
                                Ouvrir
                              </a>
                            ) : (
                              <span className="text-muted">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </div>
        </Collapse>
      </Card>
    </>
  );
};

export default GitAdminIssuesCards;
